import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { Stack } from "@mui/system";
import { Bin20Px, Inventory20Px } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";

import { StartBinMaintenanceRequest } from "~/types/api";

// "cleaning" or "movements" are the valid modes for 2-port workstations
export type TwoPortBinMaintenanceMode = Exclude<
  StartBinMaintenanceRequest["mode"],
  "mixed"
>;

type BinMaintenanceModeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mode: TwoPortBinMaintenanceMode) => void;
};

/**
 * Modal for selecting the maintenance mode for a 2-port workstation.
 */
export function BinMaintenanceModeModal(props: BinMaintenanceModeModalProps) {
  const { isOpen, onClose, onConfirm } = props;
  const { t } = useTranslation();
  const trackedModalName = useMixpanelModalName("Select Maintenance Mode");

  const [mode, setMode] = useState<TwoPortBinMaintenanceMode>("cleaning");

  const handleConfirm = () => {
    const selectedMode = mode;
    setMode("cleaning");
    onConfirm(selectedMode);
  };

  const handleClose = () => {
    setMode("cleaning");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_event, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason);
        handleClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{t("select maintenance mode")}</DialogTitle>
      <Divider />
      <DialogContent sx={{ margin: "30px 30px 0", padding: 0 }}>
        <Stack spacing={3} justifyContent="center" alignItems="center">
          <Typography
            variant="h5"
            sx={{ textAlign: "center", maxWidth: 300, fontWeight: 400 }}
          >
            {t("choose which flagged bin type to work on")}
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="large"
            onChange={(_, newValue: TwoPortBinMaintenanceMode | null) => {
              if (newValue !== null) {
                setMode(newValue);
                mixpanelTrack({
                  type: "Button Click",
                  label:
                    newValue === "cleaning" ? "Empty Bins" : "Inventory Bins",
                  trackedPageName: trackedModalName
                });
              }
            }}
            sx={(theme) => ({
              "& .MuiToggleButton-root": {
                minWidth: 180
              },
              "& .MuiToggleButton-root:not(.Mui-selected) svg path": {
                fill: theme.palette.text.disabled
              }
            })}
          >
            <ToggleButton value="cleaning">
              <Bin20Px style={{ marginRight: 8 }} />
              {t("empty bins")}
            </ToggleButton>
            <ToggleButton value="movements">
              <Inventory20Px style={{ marginRight: 8 }} />
              {t("inventory bins")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2, padding: 4 }}>
        <Button
          size="large"
          variant="subtle"
          sx={{ minWidth: 160, minHeight: 60, borderRadius: 1 }}
          startIcon={<CloseIcon />}
          onClick={() => {
            mixpanelTrack({
              type: "Button Click",
              label: "Cancel",
              trackedPageName: trackedModalName
            });
            handleClose();
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          size="large"
          variant="contained"
          sx={{ minWidth: 160, minHeight: 60, borderRadius: 1 }}
          startIcon={<CheckIcon />}
          onClick={() => {
            mixpanelTrack({
              type: "Button Click",
              label: "Confirm",
              trackedPageName: trackedModalName,
              eventProperties: {
                "Maintenance Mode":
                  mode === "cleaning" ? "Empty Bins" : "Inventory Bins"
              }
            });
            handleConfirm();
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
